import React, { useEffect, useState, useCallback, useMemo } from "react";
import { get, post } from "../../services/api";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { toast } from "react-toastify";

// Utility function to reorder items after drag-and-drop
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const MenuOrdering = () => {
  const token = localStorage.getItem("token");
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch menu data from the API with ordering
  const fetchMenuWithOrdering = useCallback(async () => {
    try {
      setLoading(true);
      const response = await get("UserAuthentication/GetMenuWithOrder", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.isSuccess) {
        throw new Error(response.error || "Failed to fetch menu.");
      }

      const sortedMenu = (response.sidebar || [])
        .sort((a, b) => a.sortOrderId - b.sortOrderId)
        .map((category) => ({
          ...category,
          subCategories: (category.subCategories || [])
            .sort((a, b) => a.sortOrderId - b.sortOrderId)
            .map((subCategory) => ({
              ...subCategory,
              forms: (subCategory.forms || []).sort(
                (a, b) => a.sortOrderId - b.sortOrderId
              ),
            })),
        }));

      setMenu(sortedMenu);
    } catch (error) {
      console.error("Error fetching menu:", error);
      setError(error.message || "Failed to load menu. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMenuWithOrdering();
  }, [fetchMenuWithOrdering]);

  const onDragEnd = useCallback((result) => {
    const { source, destination, type, draggableId } = result;

    if (!destination) return;

    console.log("Drag ended:", { type, draggableId, source, destination });

    setMenu((prevMenu) => {
      let updatedMenu = JSON.parse(JSON.stringify(prevMenu));

      if (type === "category") {
        updatedMenu = reorder(updatedMenu, source.index, destination.index);
      } else if (type === "subCategory") {
        const categoryId = parseInt(source.droppableId.split("-")[1]);
        const categoryIndex = updatedMenu.findIndex(
          (category) => category.categoryId === categoryId
        );

        if (categoryIndex !== -1) {
          updatedMenu[categoryIndex].subCategories = reorder(
            updatedMenu[categoryIndex].subCategories,
            source.index,
            destination.index
          );
        }
      } else if (type === "form") {
        const [categoryId, subCategoryId] = source.droppableId
          .split("-")
          .slice(1)
          .map(Number);
        const categoryIndex = updatedMenu.findIndex(
          (category) => category.categoryId === categoryId
        );

        if (categoryIndex !== -1) {
          const subCategoryIndex = updatedMenu[
            categoryIndex
          ].subCategories.findIndex(
            (sub) => sub.subCategoryId === subCategoryId
          );

          if (subCategoryIndex !== -1) {
            updatedMenu[categoryIndex].subCategories[subCategoryIndex].forms =
              reorder(
                updatedMenu[categoryIndex].subCategories[subCategoryIndex]
                  .forms,
                source.index,
                destination.index
              );
          }
        }
      }

      updateSortOrderId(updatedMenu);
      return updatedMenu;
    });
  }, []);

  const updateSortOrderId = (newMenuOrder) => {
    newMenuOrder.forEach((category, categoryIndex) => {
      category.sortOrderId = categoryIndex;
      category.subCategories.forEach((subCategory, subCategoryIndex) => {
        subCategory.sortOrderId = subCategoryIndex;
        subCategory.forms.forEach((form, formIndex) => {
          form.sortOrderId = formIndex;
        });
      });
    });
  };

  const updateMenuOrderOnServer = useCallback(async () => {
    if (!menu.length) {
      setError("No menu items to save.");
      return;
    }

    try {
      const formattedMenu = formatMenuForAPI(menu);
      console.log("Saving menu:", JSON.stringify(formattedMenu, null, 2)); // Debug payload

      const response = await post(
        "UserAuthentication/SaveMenuWithOrder",
        formattedMenu,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.isSuccess) {
        toast.success("Menu order saved successfully!");
        await fetchMenuWithOrdering(); // Refresh menu
      } else {
        throw new Error(response.error || "Failed to save menu order.");
      }
    } catch (error) {
      console.error("Error saving menu order:", error);
      setError(error.message || "Failed to save menu order. Please try again.");
    }
  }, [menu, fetchMenuWithOrdering, token]);

  const formatMenuForAPI = useCallback((newMenuOrder) => {
    return newMenuOrder.map((category) => ({
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      sortOrderId: category.sortOrderId,
      subCategories: category.subCategories.map((subCategory) => ({
        subCategoryId: subCategory.subCategoryId,
        subCategoryName: subCategory.subCategoryName,
        sortOrderId: subCategory.sortOrderId,
        forms: subCategory.forms.map((form) => ({
          formId: form.formId,
          formName: form.formName,
          sortOrderId: form.sortOrderId,
        })),
      })),
    }));
  }, []);

  const memoizedMenu = useMemo(() => menu, [menu]);

  if (loading) {
    return <div>Loading menu...</div>;
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        {error}
        <button className="btn btn-link" onClick={fetchMenuWithOrdering}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Menu Ordering</h2>
        <div className="ms-auto">
          <button
            className="btn btn-primary btn-sm"
            onClick={updateMenuOrderOnServer}
            disabled={!memoizedMenu.length}
          >
            Save
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="menu-ordering">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="categories" type="category">
                {(provided) => (
                  <ul
                    className="categories"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {memoizedMenu.length === 0 ? (
                      <li className="category-item text-muted">
                        No categories available
                      </li>
                    ) : (
                      memoizedMenu.map((category, index) => (
                        <Draggable
                          key={`category-${category.categoryId}`}
                          draggableId={`category-${category.categoryId}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <li
                              className={`category-item ${
                                snapshot.isDragging ? "dragging" : ""
                              }`}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <div
                                className="item-with-draggable-icon"
                                {...provided.dragHandleProps}
                              >
                                {category.categoryName}
                              </div>
                              <Droppable
                                droppableId={`subcategories-${category.categoryId}`}
                                type="subCategory"
                              >
                                {(subProvided) => (
                                  <ul
                                    ref={subProvided.innerRef}
                                    {...subProvided.droppableProps}
                                    className="subcategories"
                                  >
                                    {category.subCategories.map(
                                      (subCategory, subIndex) => (
                                        <Draggable
                                          key={`subcategory-${subCategory.subCategoryId}`}
                                          draggableId={`subcategory-${subCategory.subCategoryId}`}
                                          index={subIndex}
                                        >
                                          {(subProvided, subSnapshot) => (
                                            <li
                                              className={`sub-category-item ${
                                                subSnapshot.isDragging
                                                  ? "dragging"
                                                  : ""
                                              }`}
                                              ref={subProvided.innerRef}
                                              {...subProvided.draggableProps}
                                            >
                                              <div
                                                className="item-with-draggable-icon"
                                                {...subProvided.dragHandleProps}
                                              >
                                                {subCategory.subCategoryName}
                                              </div>
                                              <Droppable
                                                droppableId={`forms-${category.categoryId}-${subCategory.subCategoryId}`}
                                                type="form"
                                              >
                                                {(formProvided) => (
                                                  <ul
                                                    ref={formProvided.innerRef}
                                                    {...formProvided.droppableProps}
                                                    className="forms"
                                                  >
                                                    {subCategory.forms.map(
                                                      (form, formIndex) => (
                                                        <Draggable
                                                          key={`form-${form.formId}`}
                                                          draggableId={`form-${form.formId}`}
                                                          index={formIndex}
                                                        >
                                                          {(
                                                            formProvided,
                                                            formSnapshot
                                                          ) => (
                                                            <li
                                                              className={`form-item ${
                                                                formSnapshot.isDragging
                                                                  ? "dragging"
                                                                  : ""
                                                              }`}
                                                              ref={
                                                                formProvided.innerRef
                                                              }
                                                              {...formProvided.draggableProps}
                                                            >
                                                              <div
                                                                className="item-with-draggable-icon"
                                                                {...formProvided.dragHandleProps}
                                                              >
                                                                {form.formName}
                                                              </div>
                                                            </li>
                                                          )}
                                                        </Draggable>
                                                      )
                                                    )}
                                                    {formProvided.placeholder}
                                                  </ul>
                                                )}
                                              </Droppable>
                                            </li>
                                          )}
                                        </Draggable>
                                      )
                                    )}
                                    {subProvided.placeholder}
                                  </ul>
                                )}
                              </Droppable>
                            </li>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          <div className="text-end">
            <button
              className="btn btn-primary btn-sm"
              onClick={updateMenuOrderOnServer}
              disabled={!memoizedMenu.length}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MenuOrdering;
